import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridItemChangeEvent,
} from "@progress/kendo-react-grid";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { NumericTextBox, NumericTextBoxChangeEvent } from "@progress/kendo-react-inputs";
import ReactDOM from "react-dom";
import styles from "./SKUManagement.module.css";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";

// Matches the shape returned by the API
interface SkuData {
  skuId: number;
  productName: string;
  costOfGoods: number | null;
  allowOnAdHocInvoices: boolean | null;
  baseProductName: string;
  defaultMonthly: number;
  defaultOnetime: number;
  annualAnniversaryPrice: number | null;
  skuCategoryName: string;
  productId: number;
  baseProduct: number | null;
  category: number | null;
  inEdit?: boolean;
}

interface ProductOption {
  id: number | null;
  name: string;
}

interface CategoryOption {
  id: number | null;
  name: string;
}

const EDIT_FIELD = "inEdit";

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: { gridRef: React.RefObject<HTMLDivElement | null> }) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

const SKUManagement: React.FC = () => {
  const [skus, setSkus] = useState<SkuData[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<CompositeFilterDescriptor>(initialFilter);
  const [searchText, setSearchText] = useState("");
  const [editId, setEditId] = useState<number | null>(null);
  // Store original values when entering edit mode so we can restore on cancel
  const [originalItem, setOriginalItem] = useState<SkuData | null>(null);

  const gridRef = useRef<HTMLDivElement | null>(null);
  const excelExportRef = useRef<ExcelExport>(null);

  // Load all data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const [skuResponse, productsResponse, categoriesResponse] =
        await Promise.all([
          callApi("sysadmin/sku/list", HttpMethod.GET),
          callApi("sysadmin/sku/products", HttpMethod.GET),
          callApi("sysadmin/sku/categories", HttpMethod.GET),
        ]);
      setSkus(skuResponse);
      setProducts(productsResponse);
      setCategories(categoriesResponse);
    } catch (err: any) {
      const msg = err.message || "Failed to load SKU data";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // Search filter — matches across product name, category name, base product name, and SKU ID
  const filteredData = useMemo(() => {
    let data = skus;

    // Apply text search
    if (searchText.trim()) {
      const term = searchText.trim().toLowerCase();
      data = data.filter(
        (item) =>
          item.productName?.toLowerCase().includes(term) ||
          item.skuCategoryName?.toLowerCase().includes(term) ||
          item.baseProductName?.toLowerCase().includes(term) ||
          String(item.skuId).includes(term),
      );
    }

    // Apply Kendo Grid column filters
    if (filter?.filters?.length > 0) {
      data = filterBy(data, filter);
    }

    return data;
  }, [skus, filter, searchText]);

  // Mark the currently-edited row
  const dataWithEditFlag = useMemo(() => {
    return filteredData.map((item) => ({
      ...item,
      [EDIT_FIELD]: item.skuId === editId,
    }));
  }, [filteredData, editId]);

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const handleExport = () => {
    if (excelExportRef.current) {
      excelExportRef.current.save(filteredData);
    }
  };

  // Enter edit mode for a row
  const enterEdit = (dataItem: SkuData) => {
    if (editId !== null) return; // Only one row at a time
    setOriginalItem({ ...dataItem });
    setEditId(dataItem.skuId);
  };

  // Cancel editing — restore original values
  const cancelEdit = () => {
    if (originalItem) {
      setSkus((prev) =>
        prev.map((item) =>
          item.skuId === originalItem.skuId ? { ...originalItem } : item,
        ),
      );
    }
    setEditId(null);
    setOriginalItem(null);
  };

  // Save the edited row
  const saveEdit = async (dataItem: SkuData) => {
    try {
      await callApi("sysadmin/sku/update", HttpMethod.PUT, {
        skuId: dataItem.skuId,
        costOfGoods: dataItem.costOfGoods,
        allowOnAdHocInvoices: dataItem.allowOnAdHocInvoices,
        defaultMonthly: dataItem.defaultMonthly,
        defaultOnetime: dataItem.defaultOnetime,
        annualAnniversaryPrice: dataItem.annualAnniversaryPrice,
        baseProduct: dataItem.baseProduct,
        category: dataItem.category,
      });
      toast.success(`SKU ${dataItem.skuId} updated successfully`);
      setEditId(null);
      setOriginalItem(null);
      // Reload to get server-computed joined names
      await loadData();
    } catch (err: any) {
      const detail = err?.data?.message || err.message || "Unknown error";
      toast.error(`Failed to save SKU: ${detail}`);
    }
  };

  // Handle inline cell value changes
  const handleItemChange = (event: GridItemChangeEvent) => {
    const { dataItem, field, value } = event;
    if (!field) return;

    setSkus((prev) =>
      prev.map((item) =>
        item.skuId === dataItem.skuId ? { ...item, [field]: value } : item,
      ),
    );
  };

  // -- Custom cell editors --

  // Numeric editor (NumericTextBox in edit mode, fixed-decimal text otherwise)
  const makeNumericCell = useCallback(
    (decimals: number) =>
      (props: GridCellProps) => {
        const { dataItem, field } = props;
        if (dataItem[EDIT_FIELD]) {
          return (
            <td>
              <NumericTextBox
                value={dataItem[field!]}
                format={`n${decimals}`}
                step={1 / 10 ** decimals}
                spinners={false}
                onChange={(e: NumericTextBoxChangeEvent) => {
                  handleItemChange({
                    dataItem,
                    field: field!,
                    value: e.value,
                    syntheticEvent: e.syntheticEvent,
                  } as GridItemChangeEvent);
                }}
              />
            </td>
          );
        }
        const val = dataItem[field!];
        return <td>{val != null ? val.toFixed(decimals) : "NULL"}</td>;
      },
    [],
  );

  // Lookup-dropdown editor (DropDownList in edit mode, joined name otherwise)
  const makeDropdownCell = useCallback(
    <T extends { id: number | null; name: string }>(
      data: T[],
      idField: keyof SkuData,
      nameField: keyof SkuData,
    ) =>
      (props: GridCellProps) => {
        const { dataItem } = props;
        if (dataItem[EDIT_FIELD]) {
          const selected = data.find((d) => d.id === dataItem[idField]);
          return (
            <td>
              <DropDownList
                data={data}
                textField="name"
                dataItemKey="id"
                value={selected || null}
                defaultItem={{ id: null, name: "N/A" }}
                onChange={(e: DropDownListChangeEvent) => {
                  handleItemChange({
                    dataItem,
                    field: idField as string,
                    value: e.value?.id ?? null,
                    syntheticEvent: e.syntheticEvent,
                  } as unknown as GridItemChangeEvent);
                }}
                filterable
                onFilterChange={() => {}}
              />
            </td>
          );
        }
        return <td>{(dataItem[nameField] as string) || "N/A"}</td>;
      },
    [],
  );

  const CostOfGoodsCell = useMemo(() => makeNumericCell(4), [makeNumericCell]);
  const AnnualPriceCell = useMemo(() => makeNumericCell(2), [makeNumericCell]);
  const BaseProductCell = useMemo(
    () => makeDropdownCell(products, "baseProduct", "baseProductName"),
    [makeDropdownCell, products],
  );
  const CategoryCell = useMemo(
    () => makeDropdownCell(categories, "category", "skuCategoryName"),
    [makeDropdownCell, categories],
  );

  const AllowAdHocCell = useCallback(
    (props: GridCellProps) => {
      const { dataItem, field } = props;
      if (dataItem[EDIT_FIELD]) {
        return (
          <td>
            <input
              type="checkbox"
              className="form-check-input"
              checked={dataItem[field!] ?? false}
              onChange={(e) => {
                handleItemChange({
                  dataItem,
                  field: field!,
                  value: e.target.checked,
                  syntheticEvent: e,
                } as unknown as GridItemChangeEvent);
              }}
            />
          </td>
        );
      }
      const val = dataItem[field!];
      return <td>{val == null ? "NULL" : val ? "Yes" : "No"}</td>;
    },
    [],
  );

  const CommandCell = useCallback(
    (props: GridCellProps) => {
      const { dataItem } = props;
      const isInEdit = dataItem[EDIT_FIELD];
      return (
        <td>
          {isInEdit ? (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => saveEdit(dataItem)}
              >
                Update
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={cancelEdit}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => enterEdit(dataItem)}
              disabled={editId !== null}
            >
              Edit
            </button>
          )}
        </td>
      );
    },
    [editId],
  );

  return (
    <div className={styles.container}>
      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          />
        </div>
      )}

      <div className={styles.controls}>
        <div className={styles.searchWrapper}>
          <input
            type="text"
            className={`form-control ${styles.searchInput}`}
            placeholder="Search SKUs..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value.trimStart())}
            onBlur={(e) => setSearchText(e.target.value.trim())}
          />
        </div>
        <button
          onClick={handleExport}
          className="btn btn-primary"
          disabled={isLoading || skus.length === 0}
        >
          Export to Excel
        </button>
      </div>

      <ExcelExport ref={excelExportRef} fileName="SKU_Management_Export.xlsx">
        <ExcelExportColumn field="skuId" title="SKU ID" />
        <ExcelExportColumn field="productName" title="Product Name" />
        <ExcelExportColumn
          field="costOfGoods"
          title="Unit Cost of Goods"
          cellOptions={{ format: "#,##0.0000" }}
        />
        <ExcelExportColumn field="allowOnAdHocInvoices" title="Allow on AdHoc Invoices" />
        <ExcelExportColumn field="baseProductName" title="Base Product Name" />
        <ExcelExportColumn
          field="defaultMonthly"
          title="Default Monthly"
          cellOptions={{ format: "#,##0.00" }}
        />
        <ExcelExportColumn
          field="defaultOnetime"
          title="Default Onetime"
          cellOptions={{ format: "#,##0.00" }}
        />
        <ExcelExportColumn
          field="annualAnniversaryPrice"
          title="Annual Anniversary Price"
          cellOptions={{ format: "#,##0.00" }}
        />
        <ExcelExportColumn field="skuCategoryName" title="SKU (Financial) Category" />
      </ExcelExport>

      <div ref={gridRef} style={{ padding: "5px" }}>
        {isLoading ? <LoadingPanel gridRef={gridRef} /> : null}
        <Grid
          className="sysadmin-grid"
          data={dataWithEditFlag}
          dataItemKey="skuId"
          sortable={{ mode: "multiple", allowUnsort: true }}
          filterable={true}
          resizable={true}
          pageable={{ pageSizes: [10, 25, 50, 100] }}
          defaultSkip={0}
          defaultTake={25}
          filter={filter}
          onFilterChange={handleFilterChange}
          editField={EDIT_FIELD}
          onItemChange={handleItemChange}
          style={{ height: "calc(100vh - 170px)", width: "100%" }}
        >
          <Column field="skuId" title="SKU ID" width={200} editable={false} />
          <Column field="productName" title="Product Name" width={260} editable={false} />
          <Column
            field="costOfGoods"
            title="Unit Cost of Goods"
            width={160}
            filter="numeric"
            cells={{ data: CostOfGoodsCell }}
          />
          <Column
            field="allowOnAdHocInvoices"
            title="Allow on AdHoc Invoices"
            width={160}
            cells={{ data: AllowAdHocCell }}
          />
          <Column
            field="baseProduct"
            title="Base Product Name"
            width={200}
            cells={{ data: BaseProductCell }}
          />
          <Column
            field="defaultMonthly"
            title="Default Monthly"
            width={150}
            format="{0:n2}"
            filter="numeric"
          />
          <Column
            field="defaultOnetime"
            title="Default Onetime"
            width={160}
            format="{0:n2}"
            filter="numeric"
          />
          <Column
            field="annualAnniversaryPrice"
            title="Annual Anniversary Price"
            width={180}
            filter="numeric"
            cells={{ data: AnnualPriceCell }}
          />
          <Column
            field="category"
            title="SKU (Financial) Category"
            width={200}
            cells={{ data: CategoryCell }}
          />
          <Column
            title="Actions"
            width={195}
            filterable={false}
            sortable={false}
            cells={{ data: CommandCell }}
          />
        </Grid>
      </div>
    </div>
  );
};

export default SKUManagement;
